import React from 'react';
import './LandingPage.css';
import mbIcon from '../assets/mb_icon.png';

interface FeaturesPageProps {
  onBack: () => void;
  onNavigateToLogin: () => void;
  onNavigateToContact: () => void;
}

const FeaturesPage: React.FC<FeaturesPageProps> = ({ onBack, onNavigateToLogin, onNavigateToContact }) => {
  const features = {
    student: {
      title: 'Student Management',
      subtitle: 'Complete Student Information System',
      icon: 'bi-person-badge',
      color: '#5C2597',
      description: 'Comprehensive tools to manage every aspect of student data, from enrollment to graduation.',
      capabilities: [
        { icon: 'bi-upload', title: 'Bulk Upload', desc: 'Import students via Excel with validation & duplicate detection', anim: 'bounce' },
        { icon: 'bi-person-lines-fill', title: 'Profile Management', desc: 'Complete student profiles with contact, parent & academic info', anim: 'pulse' },
        { icon: 'bi-calendar-check', title: 'Attendance Tracking', desc: 'Daily attendance with statistics, reports & trend analysis', anim: 'shake' },
        { icon: 'bi-arrow-up-circle', title: 'Class Promotion', desc: 'Bulk promotion with academic archiving & fee carry-forward', anim: 'float' },
        { icon: 'bi-search', title: 'Smart Search', desc: 'Find students by name, roll number, enrollment ID or class', anim: 'swing' },
        { icon: 'bi-file-earmark-excel', title: 'Export Reports', desc: 'Download student data in Excel format for offline use', anim: 'flip' },
      ]
    },
    fee: {
      title: 'Fee Management',
      subtitle: 'Streamlined Financial Operations',
      icon: 'bi-currency-rupee',
      color: '#28A745',
      description: 'End-to-end fee management from structure creation to collection and reporting.',
      capabilities: [
        { icon: 'bi-list-check', title: 'Fee Structure', desc: 'Define fee components: Tuition, Exam, Library, Sports, Transport & more', anim: 'pulse' },
        { icon: 'bi-calendar3', title: 'Installments', desc: 'Create flexible payment schedules with custom due dates', anim: 'swing' },
        { icon: 'bi-credit-card', title: 'Online Payments', desc: 'Razorpay integration for seamless online fee collection', anim: 'flip' },
        { icon: 'bi-bell', title: 'Overdue Alerts', desc: 'Automatic tracking of pending & overdue payments', anim: 'shake' },
        { icon: 'bi-person-x', title: 'Account Control', desc: 'Disable defaulter accounts with reason tracking', anim: 'bounce' },
        { icon: 'bi-graph-up', title: 'Collection Reports', desc: 'Real-time dashboards showing collected, pending & overdue amounts', anim: 'float' },
      ]
    },
    hr: {
      title: 'HR Management',
      subtitle: 'Complete Staff Administration',
      icon: 'bi-people',
      color: '#2D7DD2',
      description: 'Manage your teaching and non-teaching staff with powerful HR tools.',
      capabilities: [
        { icon: 'bi-person-plus', title: 'Staff Onboarding', desc: 'Bulk upload teachers with Excel import & profile creation', anim: 'bounce' },
        { icon: 'bi-calendar2-check', title: 'Staff Attendance', desc: 'Mark daily attendance: Present, Absent, Leave, Half-Day', anim: 'pulse' },
        { icon: 'bi-wallet2', title: 'Salary Structure', desc: 'Define earnings (Basic, HRA, DA) & deductions (PF, Tax)', anim: 'float' },
        { icon: 'bi-cash-stack', title: 'Payroll Processing', desc: 'Generate monthly salaries with multiple payment modes', anim: 'shake' },
        { icon: 'bi-bank', title: 'Bank Verification', desc: 'Verify employee bank accounts before salary disbursement', anim: 'swing' },
        { icon: 'bi-file-earmark-text', title: 'Salary Slips', desc: 'Download individual or bulk salary slips in Excel format', anim: 'flip' },
      ]
    },
    whatsapp: {
      title: 'WhatsApp Integration',
      subtitle: 'Instant Communication Hub',
      icon: 'bi-whatsapp',
      color: '#25D366',
      description: 'Connect with parents and staff instantly through WhatsApp for seamless school communication.',
      capabilities: [
        { icon: 'bi-send', title: 'Bulk Messaging', desc: 'Send announcements to all parents or specific classes with one click', anim: 'bounce' },
        { icon: 'bi-bell-fill', title: 'Fee Reminders', desc: 'Automated payment reminders with due dates and amounts', anim: 'shake' },
        { icon: 'bi-calendar-event', title: 'Attendance Alerts', desc: 'Instant notifications to parents when student is marked absent', anim: 'pulse' },
        { icon: 'bi-card-checklist', title: 'Exam Notifications', desc: 'Share exam schedules, results and report cards via WhatsApp', anim: 'swing' },
        { icon: 'bi-chat-dots', title: 'Two-Way Chat', desc: 'Parents can respond and communicate directly with school', anim: 'float' },
        { icon: 'bi-graph-up-arrow', title: 'Delivery Reports', desc: 'Track message delivery status and read receipts', anim: 'flip' },
      ]
    }
  };

  return (
    <div className="landing-page min-vh-100 bg-light">
      {/* Navigation Header */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm">
        <div className="container-fluid px-4">
          <a className="navbar-brand d-flex align-items-center" href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>
            <img src={mbIcon} alt="MB Icon" className="mb-logo" />
            <span className="ms-2 fw-bold text-primary-custom">My Learning Board</span>
          </a>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary-custom" onClick={onBack}>
              <i className="bi bi-arrow-left me-2"></i>Back to Home
            </button>
            <button className="btn btn-primary-custom text-white" onClick={onNavigateToLogin}>
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="container-fluid px-4">
          <div className="row align-items-center">
            <div className="col-lg-8 mx-auto text-center py-4">
              <h1 className="display-4 fw-bold mb-3">Powerful Features for Modern Schools</h1>
              <p className="lead mb-4 opacity-75">
                Everything you need to manage students, fees, and staff — all powered by Agentic AI
              </p>
              <button className="btn btn-light btn-lg px-4" onClick={onNavigateToContact}>
                <i className="bi bi-calendar-event me-2"></i>Book a Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Sections */}
      <div className="container-fluid px-4 py-5">
        {/* Student Management */}
        <section id="student-management" className="mb-5 scroll-mt-5">
          <div className="row align-items-center mb-4">
            <div className="col-auto">
              <div
                className="section-icon-wrapper rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: '80px', height: '80px', backgroundColor: `${features.student.color}20` }}
              >
                <i className={`bi ${features.student.icon} fs-1 section-icon`} style={{ color: features.student.color }}></i>
              </div>
            </div>
            <div className="col">
              <h2 className="mb-1" style={{ color: features.student.color }}>{features.student.title}</h2>
              <p className="text-muted mb-0">{features.student.subtitle}</p>
            </div>
          </div>
          <p className="lead text-muted mb-4">{features.student.description}</p>
          <div className="row g-4">
            {features.student.capabilities.map((cap, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm feature-card">
                  <div className="card-body">
                    <div
                      className={`icon-box icon-${cap.anim} rounded d-inline-flex align-items-center justify-content-center mb-3`}
                      style={{ width: '56px', height: '56px', backgroundColor: `${features.student.color}15` }}
                    >
                      <i className={`bi ${cap.icon} fs-4 feature-icon`} style={{ color: features.student.color }}></i>
                    </div>
                    <h5 className="card-title">{cap.title}</h5>
                    <p className="card-text text-muted small">{cap.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <hr className="my-5" />

        {/* Fee Management */}
        <section id="fee-management" className="mb-5 scroll-mt-5">
          <div className="row align-items-center mb-4">
            <div className="col-auto">
              <div
                className="section-icon-wrapper rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: '80px', height: '80px', backgroundColor: `${features.fee.color}20` }}
              >
                <i className={`bi ${features.fee.icon} fs-1 section-icon`} style={{ color: features.fee.color }}></i>
              </div>
            </div>
            <div className="col">
              <h2 className="mb-1" style={{ color: features.fee.color }}>{features.fee.title}</h2>
              <p className="text-muted mb-0">{features.fee.subtitle}</p>
            </div>
          </div>
          <p className="lead text-muted mb-4">{features.fee.description}</p>
          <div className="row g-4">
            {features.fee.capabilities.map((cap, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm feature-card">
                  <div className="card-body">
                    <div
                      className={`icon-box icon-${cap.anim} rounded d-inline-flex align-items-center justify-content-center mb-3`}
                      style={{ width: '56px', height: '56px', backgroundColor: `${features.fee.color}15` }}
                    >
                      <i className={`bi ${cap.icon} fs-4 feature-icon`} style={{ color: features.fee.color }}></i>
                    </div>
                    <h5 className="card-title">{cap.title}</h5>
                    <p className="card-text text-muted small">{cap.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <hr className="my-5" />

        {/* HR Management */}
        <section id="hr-management" className="mb-5 scroll-mt-5">
          <div className="row align-items-center mb-4">
            <div className="col-auto">
              <div
                className="section-icon-wrapper rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: '80px', height: '80px', backgroundColor: `${features.hr.color}20` }}
              >
                <i className={`bi ${features.hr.icon} fs-1 section-icon`} style={{ color: features.hr.color }}></i>
              </div>
            </div>
            <div className="col">
              <h2 className="mb-1" style={{ color: features.hr.color }}>{features.hr.title}</h2>
              <p className="text-muted mb-0">{features.hr.subtitle}</p>
            </div>
          </div>
          <p className="lead text-muted mb-4">{features.hr.description}</p>
          <div className="row g-4">
            {features.hr.capabilities.map((cap, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm feature-card">
                  <div className="card-body">
                    <div
                      className={`icon-box icon-${cap.anim} rounded d-inline-flex align-items-center justify-content-center mb-3`}
                      style={{ width: '56px', height: '56px', backgroundColor: `${features.hr.color}15` }}
                    >
                      <i className={`bi ${cap.icon} fs-4 feature-icon`} style={{ color: features.hr.color }}></i>
                    </div>
                    <h5 className="card-title">{cap.title}</h5>
                    <p className="card-text text-muted small">{cap.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <hr className="my-5" />

        {/* WhatsApp Integration */}
        <section id="whatsapp-integration" className="mb-5 scroll-mt-5">
          <div className="row align-items-center mb-4">
            <div className="col-auto">
              <div
                className="section-icon-wrapper rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: '80px', height: '80px', backgroundColor: `${features.whatsapp.color}20` }}
              >
                <i className={`bi ${features.whatsapp.icon} fs-1 section-icon`} style={{ color: features.whatsapp.color }}></i>
              </div>
            </div>
            <div className="col">
              <h2 className="mb-1" style={{ color: features.whatsapp.color }}>{features.whatsapp.title}</h2>
              <p className="text-muted mb-0">{features.whatsapp.subtitle}</p>
            </div>
          </div>
          <p className="lead text-muted mb-4">{features.whatsapp.description}</p>
          <div className="row g-4">
            {features.whatsapp.capabilities.map((cap, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm feature-card">
                  <div className="card-body">
                    <div
                      className={`icon-box icon-${cap.anim} rounded d-inline-flex align-items-center justify-content-center mb-3`}
                      style={{ width: '56px', height: '56px', backgroundColor: `${features.whatsapp.color}15` }}
                    >
                      <i className={`bi ${cap.icon} fs-4 feature-icon`} style={{ color: features.whatsapp.color }}></i>
                    </div>
                    <h5 className="card-title">{cap.title}</h5>
                    <p className="card-text text-muted small">{cap.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* CTA Section */}
      <section className="py-5 text-white position-relative" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="container-fluid px-4 text-center">
          <h2 className="mb-3">Ready to Transform Your School?</h2>
          <p className="lead opacity-75 mb-4">Join hundreds of schools already using My Learning Board</p>
          <div className="d-flex gap-3 justify-content-center flex-wrap">
            <button className="btn btn-light btn-lg px-4" onClick={onNavigateToContact}>
              <i className="bi bi-calendar-event me-2"></i>Schedule Demo
            </button>
            <button className="btn btn-outline-light btn-lg px-4" onClick={onNavigateToLogin}>
              <i className="bi bi-box-arrow-in-right me-2"></i>Login Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer py-4">
        <div className="container-fluid px-4 text-center">
          <p className="mb-0 small">
            &copy; 2026 Omnix Digital Solutions. All rights reserved.
          </p>
        </div>
      </footer>

      <style>{`
        .scroll-mt-5 {
          scroll-margin-top: 80px;
        }

        /* Section Header Icon Animation */
        .section-icon-wrapper {
          animation: pulse-glow 2s ease-in-out infinite;
        }
        .section-icon {
          animation: float 3s ease-in-out infinite;
        }

        /* Feature Card Hover Effects */
        .feature-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          cursor: pointer;
        }
        .feature-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 15px 40px rgba(0,0,0,0.12) !important;
        }

        /* Icon Box Base Styles */
        .icon-box {
          transition: all 0.3s ease;
        }
        .feature-icon {
          transition: all 0.3s ease;
        }

        /* Bounce Animation */
        .feature-card:hover .icon-bounce {
          animation: bounce 0.6s ease;
        }

        /* Pulse Animation */
        .feature-card:hover .icon-pulse {
          animation: pulse 0.6s ease;
        }
        .feature-card:hover .icon-pulse .feature-icon {
          transform: scale(1.2);
        }

        /* Shake Animation */
        .feature-card:hover .icon-shake {
          animation: shake 0.5s ease;
        }

        /* Float Animation */
        .icon-float .feature-icon {
          animation: float 2s ease-in-out infinite;
        }

        /* Swing Animation */
        .feature-card:hover .icon-swing {
          animation: swing 0.6s ease;
        }

        /* Flip Animation */
        .feature-card:hover .icon-flip {
          animation: flip 0.6s ease;
        }

        /* Keyframes */
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          30% { transform: translateY(-12px); }
          50% { transform: translateY(-6px); }
          70% { transform: translateY(-8px); }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }

        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.4); }
          50% { box-shadow: 0 0 20px 5px rgba(102, 126, 234, 0.2); }
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-5px) rotate(-2deg); }
          40% { transform: translateX(5px) rotate(2deg); }
          60% { transform: translateX(-3px) rotate(-1deg); }
          80% { transform: translateX(3px) rotate(1deg); }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }

        @keyframes swing {
          0%, 100% { transform: rotate(0deg); }
          20% { transform: rotate(15deg); }
          40% { transform: rotate(-10deg); }
          60% { transform: rotate(5deg); }
          80% { transform: rotate(-5deg); }
        }

        @keyframes flip {
          0% { transform: perspective(400px) rotateY(0); }
          100% { transform: perspective(400px) rotateY(360deg); }
        }

        /* Icon color transition on hover */
        .feature-card:hover .icon-box {
          transform: scale(1.1);
        }

        /* Stagger animation delay for cards */
        .feature-card:nth-child(1) { animation-delay: 0s; }
        .feature-card:nth-child(2) { animation-delay: 0.1s; }
        .feature-card:nth-child(3) { animation-delay: 0.2s; }
        .feature-card:nth-child(4) { animation-delay: 0.3s; }
        .feature-card:nth-child(5) { animation-delay: 0.4s; }
        .feature-card:nth-child(6) { animation-delay: 0.5s; }
      `}</style>
    </div>
  );
};

export default FeaturesPage;
